import { Card, CardContent } from "@/components/ui/card";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface SectorComparisonProps {
  yourEmissions: number;
  averageEmissions: number;
  sector: string;
}

/**
 * Sector Comparison Component
 * Compares user emissions with sector average
 */
export default function SectorComparison({
  yourEmissions,
  averageEmissions,
  sector,
}: SectorComparisonProps) {
  const { t } = useTranslation();
  const difference = ((yourEmissions - averageEmissions) / averageEmissions) * 100;
  const isLower = difference < 0;
  const isNear = Math.abs(difference) < 5;

  const getIcon = () => {
    if (isNear) return <Minus className="h-4 w-4" />;
    return isLower ? <TrendingDown className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />;
  };

  const getColor = () => {
    if (isNear) return "text-blue-600 bg-blue-50 dark:bg-blue-950/20";
    return isLower
      ? "text-green-600 bg-green-50 dark:bg-green-950/20"
      : "text-red-600 bg-red-50 dark:bg-red-950/20";
  };

  const getMessage = () => {
    if (isNear) return t('dashboard.emissions_near_average', { sector });
    return isLower
      ? t('dashboard.emissions_lower_than_average', { percent: Math.abs(difference).toFixed(0), sector })
      : t('dashboard.emissions_higher_than_average', { percent: Math.abs(difference).toFixed(0), sector });
  };

  return (
    <Card className={`border-l-4 ${isLower ? "border-l-green-500" : isNear ? "border-l-blue-500" : "border-l-red-500"}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${getColor()}`}>
            {getIcon()}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">{getMessage()}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              <span>{t('dashboard.your')}: {yourEmissions.toFixed(1)} tCO₂e</span>
              <span>|</span>
              <span>{t('dashboard.average')}: {averageEmissions.toFixed(1)} tCO₂e</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

